import React, { useEffect, useMemo, useState } from "react";
import { Popup } from "devextreme-react/popup";
import { TextBox } from "devextreme-react/text-box";
import { Button } from "devextreme-react/button";
import { FiCheck, FiX } from "react-icons/fi";
import notify from "devextreme/ui/notify";
import { userService } from "../../services/userService";
import { Users } from "../../dtos/User";

const WARNING_MESSAGE =
  "Requieres al menos un carácter especial, una letra mayúscula y un número.";
const SUCCESS_MESSAGE = "La contraseña ha sido actualizada con éxito.";

type Criterio = { label: string; ok: boolean };

const evaluarPassword = (pwd: string): Criterio[] => [
  { label: "Entre 8 y 12 caracteres", ok: pwd.length >= 8 && pwd.length <= 12 },
  { label: "Al menos una letra mayúscula (A-Z)", ok: /[A-Z]/.test(pwd) },
  { label: "Al menos una letra minúscula (a-z)", ok: /[a-z]/.test(pwd) },
  { label: "Al menos un número (0-9)", ok: /\d/.test(pwd) },
  {
    label: "Al menos un carácter especial (@, #, $, %, &, *, ...)",
    ok: /[^A-Za-z0-9]/.test(pwd),
  },
];

type ChangePasswordPopupProps = {
  visible: boolean;
  usuario: Partial<Users> | null;
  onHiding: () => void;
  onSuccess?: () => void;
};

const ChangePasswordPopup: React.FC<ChangePasswordPopupProps> = ({
  visible,
  usuario,
  onHiding,
  onSuccess,
}) => {
  const [password, setPassword] = useState<string>("");
  const [touched, setTouched] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [successVisible, setSuccessVisible] = useState<boolean>(false);

  useEffect(() => {
    if (visible) {
      setPassword("");
      setTouched(false);
      setShowPassword(false);
      setSaving(false);
    }
  }, [visible]);

  const criterios = useMemo(() => evaluarPassword(password), [password]);
  const isValid = criterios.every((c) => c.ok);

  const handleSave = async () => {
    if (!usuario?.id) return;
    if (!isValid) {
      setTouched(true);
      return;
    }
    setSaving(true);
    const resp = await userService.changePassword(usuario.id, password);
    setSaving(false);

    if (resp.error) {
      notify(resp.error.message, "error", 3500);
      return;
    }
    setSuccessVisible(true);
  };

  const handleSuccessClose = () => {
    setSuccessVisible(false);
    onHiding();
    onSuccess?.();
  };

  return (
    <>
      <Popup
        visible={visible}
        onHiding={onHiding}
        dragEnabled={false}
        showCloseButton
        showTitle
        title={`Cambiar contraseña: ${usuario?.nombre ?? ""}`}
        width={480}
        height="auto"
      >
        <div style={{ padding: "20px" }}>
          <label
            style={{
              display: "block",
              fontWeight: 600,
              marginBottom: 8,
              color: "#172B4D",
            }}
          >
            Nueva contraseña
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            <TextBox
              mode={showPassword ? "text" : "password"}
              value={password}
              placeholder="Ingrese la nueva contraseña"
              onValueChanged={(e) => {
                setPassword(e.value ?? "");
                setTouched(true);
              }}
              style={{ flex: 1 }}
              maxLength={12}
            />
            <Button
              icon={showPassword ? "eyeclose" : "eyeopen"}
              stylingMode="outlined"
              onClick={() => setShowPassword((v) => !v)}
            />
          </div>

          {touched && !isValid && (
            <div
              style={{
                marginTop: 12,
                padding: "10px 12px",
                borderRadius: 8,
                background: "#FFF4E5",
                border: "1px solid #F8981D",
                color: "#7a4a00",
                fontSize: 13,
              }}
            >
              {WARNING_MESSAGE}
            </div>
          )}

          <ul style={{ listStyle: "none", padding: 0, margin: "16px 0 0" }}>
            {criterios.map((c) => (
              <li
                key={c.label}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 13,
                  color: c.ok ? "#16a34a" : "#6B778C",
                  marginBottom: 6,
                }}
              >
                {c.ok ? <FiCheck size={16} /> : <FiX size={16} />}
                {c.label}
              </li>
            ))}
          </ul>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 12,
              marginTop: 20,
            }}
          >
            <Button
              text="Cancelar"
              stylingMode="text"
              onClick={onHiding}
              disabled={saving}
            />
            <Button
              text={saving ? "Guardando..." : "Guardar"}
              type="success"
              stylingMode="contained"
              onClick={handleSave}
              disabled={!isValid || saving}
            />
          </div>
        </div>
      </Popup>

      <Popup
        visible={successVisible}
        onHiding={handleSuccessClose}
        dragEnabled={false}
        showCloseButton={false}
        showTitle
        title="Contraseña actualizada"
        width={420}
        height="auto"
      >
        <div style={{ padding: 24, textAlign: "center" }}>
          <FiCheck size={42} color="#16a34a" />
          <p style={{ margin: "12px 0 0", fontSize: 15, color: "#172B4D" }}>
            {SUCCESS_MESSAGE}
          </p>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: 20,
            }}
          >
            <Button
              text="Aceptar"
              type="success"
              stylingMode="contained"
              onClick={handleSuccessClose}
            />
          </div>
        </div>
      </Popup>
    </>
  );
};

export default ChangePasswordPopup;
